/** Claves internas en productos/listas_precios — la UI puede mostrar otras etiquetas (p. ej. Lista 1). */
export const LISTA_PRECIO_KEYS = ['mayorista', 'interior', 'posadas'];

export const LISTA_PRECIO_ETIQUETA_MAX_LEN = 30;

const FALLBACK_LABELS = {
  mayorista: 'Lista 1',
  interior: 'Lista 2',
  posadas: 'Lista 3'
};

export const DEFAULT_LISTAS_PRECIO_ETIQUETAS = { ...FALLBACK_LABELS };

export function etiquetaLista(empresaConfig, key) {
  const raw = empresaConfig?.listas_precios_etiquetas?.[key];
  if (raw != null && String(raw).trim() !== '') return String(raw).trim();
  return FALLBACK_LABELS[key] || key;
}

/** Valida lista por defecto en PV (solo claves conocidas). */
export function listaPuntoVentaDefaultValida(key) {
  const k = String(key || '').trim();
  return LISTA_PRECIO_KEYS.includes(k) ? k : null;
}

/** Redondeo monetario a 2 decimales (evita 1999.99 por float). */
export function roundProductPrice(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100) / 100;
}

/** Parseo numérico tolerante (coma/punto) para precios de producto. */
export function parseProductPrice(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0 ? roundProductPrice(raw) : 0;
  }
  const str = String(raw).trim();
  let parsed = parseFloat(str.replace(',', '.'));
  if (Number.isFinite(parsed) && parsed > 0) return roundProductPrice(parsed);
  parsed = parseFloat(str.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? roundProductPrice(parsed) : 0;
}

/**
 * Precio canónico de venta según lista default de empresa (PV, cajero, catálogo).
 */
export function resolveProductSalePrice(producto, listaKey = 'mayorista') {
  const listas = producto?.listas_precios && typeof producto.listas_precios === 'object'
    ? producto.listas_precios
    : {};
  const key = listaPuntoVentaDefaultValida(listaKey) || 'mayorista';

  const fromLista = parseProductPrice(listas[key]);
  if (fromLista > 0) return fromLista;

  const candidates = [
    producto?.precio_venta,
    producto?.precio,
    producto?.precio_unitario,
    producto?.precioUnitario,
    producto?.precio_final,
    ...LISTA_PRECIO_KEYS.map((k) => listas[k])
  ];

  for (const candidate of candidates) {
    const value = parseProductPrice(candidate);
    if (value > 0) return value;
  }
  return 0;
}

/** Normaliza etiquetas para formulario (defaults Lista 1–3 si faltan en Firestore). */
export function parseListasPreciosFromEmpresaConfig(data) {
  const raw = data?.listas_precios_etiquetas;
  const listas_precios_etiquetas = {};
  LISTA_PRECIO_KEYS.forEach((key) => {
    const v = raw?.[key];
    listas_precios_etiquetas[key] =
      v != null && String(v).trim() !== '' ? String(v).trim() : FALLBACK_LABELS[key];
  });
  const lista_precio_punto_venta_default =
    listaPuntoVentaDefaultValida(data?.lista_precio_punto_venta_default) || 'mayorista';
  return { listas_precios_etiquetas, lista_precio_punto_venta_default };
}

/**
 * Valida config de listas antes de guardar.
 * @returns {{ valid: true, normalized: object } | { valid: false, error: string }}
 */
export function validarListasPreciosConfig(config) {
  const etiquetas = config?.listas_precios_etiquetas || {};
  const normalizedEtiquetas = {};

  for (const key of LISTA_PRECIO_KEYS) {
    const slot = FALLBACK_LABELS[key];
    const label = String(etiquetas[key] ?? '').trim();
    if (!label) {
      return { valid: false, error: `La etiqueta de ${slot} no puede estar vacía` };
    }
    if (label.length > LISTA_PRECIO_ETIQUETA_MAX_LEN) {
      return {
        valid: false,
        error: `La etiqueta de ${slot} supera ${LISTA_PRECIO_ETIQUETA_MAX_LEN} caracteres`
      };
    }
    normalizedEtiquetas[key] = label;
  }

  const lista_precio_punto_venta_default = listaPuntoVentaDefaultValida(
    config?.lista_precio_punto_venta_default
  );
  if (!lista_precio_punto_venta_default) {
    return {
      valid: false,
      error: 'Seleccioná una lista predeterminada válida para el punto de venta'
    };
  }

  return {
    valid: true,
    normalized: {
      listas_precios_etiquetas: normalizedEtiquetas,
      lista_precio_punto_venta_default
    }
  };
}
